import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const DepartmentsContext = createContext();

export const DepartmentsProvider = ({ children }) => {
  const [departments, setDepartments] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    all: 1,
  });

  const handleSearchChange = (value) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      search: value,
      currentPage: 1, // Reset to first page when searching
    }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      handleSearchChange(e.target.value);
    }
  };

  const fetchDepartments = useCallback(async () => {
    setDepartments((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/departments${queryString}`);
      if (response.success) {
        setDepartments({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page || 1,
        }));
      }
    } catch (error) {
      setDepartments({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = departments.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <DepartmentsContext.Provider
      value={{
        departments,
        filterOptions,
        handleSearch,
        handleSearchChange,
        refetch: fetchDepartments,
        Pagination,
      }}
    >
      {children}
    </DepartmentsContext.Provider>
  );
};

export const useDepartments = () => {
  const context = useContext(DepartmentsContext);
  if (!context) {
    throw new Error("useDepartments must be used within DepartmentsProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/ahr/departments?search=${inputValue}`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};